import React, { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Paper,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { RoutePaths } from "../../../../enumStrings";
import {
  CmpEventStatusEnum,
  CmpEventVisibilityEnum,
  GetCmpManageEventsQuery,
  useCancelCmpEventMutation,
  useCompleteCmpEventMutation,
  useDeleteCmpEventMutation,
  useExportCmpEventAttendeesCsvLazyQuery,
  useExportCmpEventSponsorsCsvLazyQuery,
  useExportCmpEventsCsvLazyQuery,
  useGetCmpEventsAnalyticsQuery,
  useGetCmpEventTypesQuery,
  useGetCmpManageEventsQuery,
  useGetChamberMemberProSettingsQuery,
  usePublishCmpEventMutation,
  useUnpublishCmpEventMutation,
} from "../../../../graphql/types";
import useGetChamberFromUserIdInAuth from "../../../../hooks/useGetChamberFromUserIdInAuth";
import { commonStyles } from "../../../common/commonStyles";
import { normalizeCmpTimeZone } from "../../../../utils/cmpTimeZones";
import { toEventTypeOptions } from "../eventBackendMappers";
import {
  cpaGold,
  cpaGoldDark,
  formatCurrencyFromCents,
} from "../EventSales/eventSalesShared";
import {
  DataTable,
  DataTableColumn,
} from "../../../common/dashboard/DataTable";
import {
  ClearFiltersButton,
  FilterBar,
  FilterMenuButton,
  FilterSearchField,
  clearFiltersButtonSx,
  filterFieldSx,
} from "../../../common/dashboard/FilterBar";
import { ManagePageShell } from "../../../common/dashboard/ManagePageShell";
import {
  buildRowActions,
  RowActionMenu,
  RowActionsTrigger,
} from "../../../common/dashboard/RowActionMenu";
import { StatusBadge } from "../../../common/dashboard/StatusBadge";
import CalendarDateInput from "../../MyChamber/ChamberSettings/common/CalendarDateInput";
import EventDashboardPixelQa, {
  getDashboardPixelQaState,
} from "../pixelQa/EventDashboardPixelQa";

type ManageEventRow =
  GetCmpManageEventsQuery["getCmpManageEvents"]["events"][number];

// Q-006: the manage screenshot's "Scheduled" status/menu is intentionally
// omitted — backend has no scheduled state, so published events map straight
// to Live/Private.
type DerivedStatus =
  | "live"
  | "private"
  | "canceled"
  | "closed"
  | "draft"
  | "unpublished";

type ActionKey =
  | "edit"
  | "exportSponsors"
  | "exportAttendees"
  | "publishPublic"
  | "publishPrivate"
  | "cancel"
  | "close"
  | "setDraft"
  | "delete";

type SortColumn =
  | "name"
  | "date"
  | "ticketsSold"
  | "ticketRevenue"
  | "sponsorsSold"
  | "sponsorshipRevenue"
  | "totalRevenue"
  | "status";

type SortState = { column: SortColumn; direction: "asc" | "desc" };

// Status renders as colored text (no pill) per the manage screenshot:
// Live green, Private gold, Canceled black, Closed red, Draft grey.
const statusMeta: Record<DerivedStatus, { label: string; color: string }> = {
  live: { label: "Live", color: commonStyles.liveGreen },
  private: { label: "Private", color: cpaGold },
  canceled: { label: "Canceled", color: "#111827" },
  closed: { label: "Closed", color: "#DC2626" },
  draft: { label: "Draft", color: "#6B7280" },
  unpublished: { label: "Unpublished", color: "#6B7280" },
};

const deriveStatus = (event: ManageEventRow): DerivedStatus => {
  if (event.status === CmpEventStatusEnum.Canceled) {
    return "canceled";
  }
  if (event.status === CmpEventStatusEnum.Completed) {
    return "closed";
  }
  if (event.status === CmpEventStatusEnum.Draft) {
    return "draft";
  }
  // July 9, 2026 direction: published events whose end date/time has passed
  // are auto-unpublished. The backend derives the same state to hide them
  // from the public site; the stored status never flips.
  if (
    typeof event.endAtUtcMilli === "number" &&
    event.endAtUtcMilli < Date.now()
  ) {
    return "unpublished";
  }
  return event.visibility === CmpEventVisibilityEnum.Private ? "private" : "live";
};

// Status-dependent Actions menus per the manage screenshot. Q-009: Live and
// Private events cannot be deleted; Q-006: "Schedule Event" is omitted.
const actionsByStatus: Record<DerivedStatus, ActionKey[]> = {
  live: ["edit", "exportSponsors", "exportAttendees", "cancel", "close"],
  private: ["edit", "exportSponsors", "exportAttendees", "cancel", "close"],
  canceled: ["edit", "setDraft", "delete"],
  closed: ["edit", "setDraft", "delete"],
  draft: [
    "edit",
    "publishPublic",
    "publishPrivate",
    "cancel",
    "delete",
  ],
  // Ended (auto-unpublished) events keep their exports and can be formally
  // closed or set back to draft; no delete (Q-009 — they were live).
  unpublished: ["edit", "exportSponsors", "exportAttendees", "close", "setDraft"],
};

const actionLabel: Record<ActionKey, string> = {
  edit: "Edit Event",
  exportSponsors: "Export Sponsor List",
  exportAttendees: "Export Attendee List",
  publishPublic: "Publish Public Event",
  publishPrivate: "Publish Private Event",
  cancel: "Cancel Event",
  close: "Close Event",
  setDraft: "Set Back to Draft",
  delete: "Delete Event",
};

const dangerActions = new Set<ActionKey>(["cancel", "delete"]);

// Shared row-action wiring (B7): RowActionMenu's paper/destructive defaults
// are documented verbatim-ManageEvents copies, so only the matrix + icon
// mapping ride along here. Module scope is SAFE for this const — it touches
// only page-local consts and icon components, never RoutePaths/enumStrings
// (the circular-import TDZ trap documented in ManagePageShell.tsx).
const getRowActions = buildRowActions<DerivedStatus, ActionKey, ManageEventRow>(
  {
    matrix: actionsByStatus,
    labels: actionLabel,
    getStatus: deriveStatus,
    destructiveActions: dangerActions,
    // Screenshot shows a divider isolating Delete Event at the bottom of the
    // Draft/Canceled/Closed menus.
    dividerBeforeActions: new Set<ActionKey>(["delete"]),
    getIcon: (action) =>
      action === "edit" ? (
        <ListItemIcon sx={{ color: "#111827", minWidth: 32 }}>
          <EditOutlinedIcon fontSize="small" />
        </ListItemIcon>
      ) : action === "delete" ? (
        <ListItemIcon sx={{ color: "#DC2626", minWidth: 32 }}>
          <DeleteOutlineIcon fontSize="small" />
        </ListItemIcon>
      ) : undefined,
  }
);

// Dates/times must render in the CHAMBER's time zone (matching what the
// wizard captured), not the viewer's browser zone.
const makeEventDateTimeFormatters = (timeZone: string) => {
  const dateFormatter = new Intl.DateTimeFormat("en-CA", {
    month: "long",
    day: "2-digit",
    year: "numeric",
    timeZone,
  });
  // en-US on purpose: the screenshot time range reads "6:00 PM – 10:00 PM"
  // (en-CA renders "6:00 p.m.").
  const timeFormatter = new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit",
    timeZone,
  });
  return {
    formatDate: (utcMilli: number) => dateFormatter.format(new Date(utcMilli)),
    // En dash separator per the manage screenshot.
    formatTimeRange: (startUtcMilli: number, endUtcMilli: number) =>
      `${timeFormatter.format(new Date(startUtcMilli))} – ${timeFormatter.format(
        new Date(endUtcMilli)
      )}`,
  };
};

// Tickets-sold fill tiers from the screenshot rows (75% green / 50% gold /
// 27% red / 0% grey) — both the slim bar and the percent label share the tier.
const ticketsSoldTierColor = (pct: number): string => {
  if (pct >= 60) {
    return commonStyles.liveGreen;
  }
  if (pct >= 40) {
    return cpaGold;
  }
  if (pct > 0) {
    return "#DC2626";
  }
  return "#6B7280";
};

// Filter-row control styling now comes from the shared FilterBar module
// (filterFieldSx / filterButtonSx / activeFilterButtonSx /
// clearFiltersButtonSx are byte-identical to the deleted local copies except
// the shared width:100% + idle color #6B7280, both undone per-instance via
// the FilterMenuButton/ClearFiltersButton sx escape hatches below).

const exportButtonSx = {
  backgroundColor: cpaGold,
  borderRadius: "6px",
  color: "white",
  fontSize: 15,
  fontWeight: 700,
  minHeight: 48,
  px: 2.75,
  textTransform: "none",
  whiteSpace: "nowrap",
  "&:hover": { backgroundColor: cpaGoldDark },
} as const;

// Mirrors the Manage Invoices date-range menu so CalendarDateInput popovers
// are not clipped by the menu surface.
const dateRangeMenuPaperSx = {
  borderRadius: "8px",
  boxShadow: "0 12px 28px rgba(15, 23, 42, 0.16)",
  mt: 0.75,
  overflow: "visible",
  p: 2,
  width: 320,
} as const;

// Row "Actions ⌄" trigger sx, extracted verbatim from the old inline Button
// sx for the shared RowActionsTrigger. The old local actionMenuPaperSx /
// paginationSx / pageSizeSelectSx duplicates are gone — RowActionMenu's
// rowActionMenuPaperSx and DataTable's dataTablePaginationSx /
// dataTablePageSizeSelectSx defaults are byte-identical ManageEvents copies.
const eventsActionButtonSx = {
  backgroundColor: "white",
  borderColor: "#D1D5DB",
  borderRadius: "8px",
  color: "#111827",
  fontWeight: 600,
  height: 44,
  px: 2.5,
  textTransform: "none",
  whiteSpace: "nowrap",
  "&:hover": {
    backgroundColor: "white",
    borderColor: "#6B7280",
  },
} as const;

const downloadCsv = (csv: {
  filename: string;
  contentType: string;
  csvContent: string;
}) => {
  const blob = new Blob([csv.csvContent], { type: csv.contentType });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = csv.filename;
  anchor.click();
  URL.revokeObjectURL(url);
};

const AnalyticsCard = ({
  label,
  value,
  current,
  prior,
}: {
  label: string;
  value: string;
  current: number;
  prior: number;
}) => {
  const change =
    prior === 0 ? (current > 0 ? 100 : 0) : ((current - prior) / prior) * 100;
  const pct = Math.abs(Math.round(change));
  // Verbatim delta casing from the manage screenshot (spacing quirks
  // normalized): positive "X% Increase from last year" in green, negative
  // "X% decrease from last year" in red, zero stays grey with "Increase".
  const deltaText =
    change < 0
      ? `${pct}% decrease from last year`
      : `${pct}% Increase from last year`;
  const deltaColorClassName =
    change > 0
      ? "text-green-600"
      : change < 0
        ? "text-red-500"
        : "text-gray-500";
  return (
    <Paper elevation={0} className="rounded-xl border border-gray-200 p-4">
      <p className="text-sm font-bold text-gray-950">{label}</p>
      <p className="mt-2 text-3xl font-extrabold text-gray-950">{value}</p>
      <p className={`mt-2 text-sm font-semibold ${deltaColorClassName}`}>
        {deltaText}
      </p>
    </Paper>
  );
};

// Update Status confirmation from the manage screenshot: red circular X close
// top-right, big red circle with a white warning triangle, gold YES / black
// CANCEL. Delete keeps the same shell with a trash icon and delete wording.
// The shared ConfirmDialog cannot render the red circular close button, hence
// this page-local modal.
const UpdateStatusModal = ({
  open,
  variant,
  title,
  loading,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  variant: "status" | "delete";
  title: string;
  loading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) => (
  <Dialog
    open={open}
    onClose={onCancel}
    maxWidth="sm"
    fullWidth
    PaperProps={{ sx: { borderRadius: "12px" } }}
  >
    <DialogContent className="relative">
      <IconButton
        size="small"
        onClick={onCancel}
        aria-label="close"
        sx={{
          backgroundColor: "#EF4444",
          color: "white",
          height: 36,
          position: "absolute",
          right: 16,
          top: 16,
          width: 36,
          "&:hover": { backgroundColor: "#DC2626" },
        }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
      <div className="flex flex-col items-center px-4 py-8 text-center">
        <div className="mb-8 flex h-28 w-28 items-center justify-center rounded-full bg-[#EF4444] text-white">
          {variant === "delete" ? (
            <DeleteOutlineIcon sx={{ fontSize: 56 }} />
          ) : (
            <WarningAmberIcon sx={{ fontSize: 56 }} />
          )}
        </div>
        <h3 className="max-w-md text-2xl font-bold leading-relaxed text-gray-950">
          {title}
        </h3>
        <div className="mt-10 flex w-full max-w-md gap-4">
          <Button
            fullWidth
            variant="contained"
            disabled={loading}
            onClick={onConfirm}
            startIcon={
              loading ? <CircularProgress color="inherit" size={16} /> : undefined
            }
            sx={{
              backgroundColor: cpaGold,
              borderRadius: "10px",
              fontSize: 16,
              fontWeight: 700,
              py: 1.5,
              textTransform: "uppercase",
              "&:hover": { backgroundColor: cpaGoldDark },
            }}
          >
            Yes
          </Button>
          <Button
            fullWidth
            variant="contained"
            disabled={loading}
            onClick={onCancel}
            sx={{
              // Charcoal token unifies this Cancel button with the
              // Members/Invoicing dark modal buttons (POL-006, sanctioned
              // #1F2937 → #24272E shade change).
              backgroundColor: commonStyles.charcoal,
              borderRadius: "10px",
              fontSize: 16,
              fontWeight: 700,
              py: 1.5,
              textTransform: "uppercase",
              "&:hover": { backgroundColor: "#000000" },
            }}
          >
            Cancel
          </Button>
        </div>
      </div>
    </DialogContent>
  </Dialog>
);

const ManageEventsContent = (): React.ReactElement => {
  const navigate = useNavigate();
  const chamber = useGetChamberFromUserIdInAuth();
  const chamberId = chamber?.id || "";

  const { data: settingsData } = useGetChamberMemberProSettingsQuery({
    variables: { chamberId },
    skip: !chamberId,
  });
  const chamberTimeZone = normalizeCmpTimeZone(
    settingsData?.getChamberMemberProSettings.setup.timeZone ||
      Intl.DateTimeFormat().resolvedOptions().timeZone ||
      "America/Toronto"
  ).timeZone;
  const { formatDate, formatTimeRange } = useMemo(
    () => makeEventDateTimeFormatters(chamberTimeZone),
    [chamberTimeZone]
  );

  const [searchQuery, setSearchQuery] = useState("");
  const [locationFilter, setLocationFilter] = useState("all");
  const [eventTypeFilter, setEventTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState<DerivedStatus | "all">("all");
  const [dateFromFilter, setDateFromFilter] = useState("");
  const [dateToFilter, setDateToFilter] = useState("");
  const [sort, setSort] = useState<SortState | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [feedback, setFeedback] = useState<{
    severity: "success" | "error";
    message: string;
  } | null>(null);
  const [locationMenuAnchor, setLocationMenuAnchor] =
    useState<HTMLElement | null>(null);
  const [typeMenuAnchor, setTypeMenuAnchor] = useState<HTMLElement | null>(null);
  const [statusMenuAnchor, setStatusMenuAnchor] = useState<HTMLElement | null>(
    null
  );
  const [dateMenuAnchor, setDateMenuAnchor] = useState<HTMLElement | null>(null);
  const [confirmAction, setConfirmAction] = useState<{
    event: ManageEventRow;
    action: ActionKey;
  } | null>(null);
  const [actionMenu, setActionMenu] = useState<{
    event: ManageEventRow;
    el: HTMLElement;
  } | null>(null);

  // ----- Page header ("Manage Events" band with the gold add button) --------
  const pageHeaderActions = useMemo(
    () => (
      <Button
        variant="contained"
        startIcon={<AddIcon />}
        onClick={() => navigate(RoutePaths.chamberAdmin_events_create)}
        sx={{
          alignSelf: { xs: "flex-start", sm: "center" },
          backgroundColor: cpaGold,
          borderRadius: "8px",
          fontSize: 16,
          fontWeight: 700,
          minHeight: 52,
          px: 3,
          textTransform: "none",
          whiteSpace: "nowrap",
          "&:hover": { backgroundColor: cpaGoldDark },
        }}
      >
        Add New Event
      </Button>
    ),
    [navigate]
  );

  // Handed to ManagePageShell, which owns the set/cleanup effect. Built
  // INSIDE the component with useMemo (NOT at module scope): the actions
  // button reaches RoutePaths via navigate, and enumStrings imports this page
  // for the route registry — module-init evaluation hits the circular-import
  // TDZ and white-screens the app on cold load (see ManagePageShell.tsx).
  // Title is deliberately NOT set — it stays route-sourced ("Manage Events").
  const headerOverride = useMemo(
    () => ({
      description: "View, manage and track all of your chamber events.",
      actions: pageHeaderActions,
      withHeaderDivider: true,
      titleClassName: "text-[34px] font-extrabold leading-tight text-black",
    }),
    [pageHeaderActions]
  );

  // ----- Queries -------------------------------------------------------------
  const {
    data: eventsData,
    loading: eventsLoading,
    refetch: refetchEvents,
  } = useGetCmpManageEventsQuery({
    variables: { input: { chamberId } },
    skip: !chamberId,
    fetchPolicy: "cache-and-network",
  });
  const events = useMemo(
    () => eventsData?.getCmpManageEvents.events || [],
    [eventsData]
  );

  const { data: analyticsData, refetch: refetchAnalytics } =
    useGetCmpEventsAnalyticsQuery({
      variables: { input: { chamberId } },
      skip: !chamberId,
      fetchPolicy: "cache-and-network",
    });
  const analytics = analyticsData?.getCmpEventsAnalytics;

  const { data: eventTypesData } = useGetCmpEventTypesQuery({
    variables: { input: { chamberId, includeArchived: false } },
    skip: !chamberId,
    fetchPolicy: "cache-and-network",
  });
  const eventTypeOptions = useMemo(
    () => toEventTypeOptions(eventTypesData?.getCmpEventTypes.eventTypes || []),
    [eventTypesData]
  );
  const locationOptions = useMemo(
    () =>
      Array.from(new Set(events.map((event) => event.locationLabel))).filter(
        Boolean
      ),
    [events]
  );

  // ----- Mutations + exports -------------------------------------------------
  const [publishEvent] = usePublishCmpEventMutation();
  const [unpublishEvent] = useUnpublishCmpEventMutation();
  const [cancelEvent] = useCancelCmpEventMutation();
  const [completeEvent] = useCompleteCmpEventMutation();
  const [deleteEvent] = useDeleteCmpEventMutation();
  const [exportAllEvents, { loading: exportingAll }] =
    useExportCmpEventsCsvLazyQuery({ fetchPolicy: "network-only" });
  const [exportAttendees] = useExportCmpEventAttendeesCsvLazyQuery({
    fetchPolicy: "network-only",
  });
  const [exportSponsors] = useExportCmpEventSponsorsCsvLazyQuery({
    fetchPolicy: "network-only",
  });
  const [actionLoading, setActionLoading] = useState(false);

  // ----- Filtering + sorting ---------------------------------------------------
  const filteredEvents = useMemo(() => {
    const term = searchQuery.trim().toLowerCase();
    const fromUtcMilli = dateFromFilter
      ? new Date(`${dateFromFilter}T00:00:00`).getTime()
      : null;
    const toUtcMilli = dateToFilter
      ? new Date(`${dateToFilter}T23:59:59.999`).getTime()
      : null;
    return events.filter((event) => {
      if (term && !event.title.toLowerCase().includes(term)) {
        return false;
      }
      if (locationFilter !== "all" && event.locationLabel !== locationFilter) {
        return false;
      }
      if (
        eventTypeFilter !== "all" &&
        event.eventTypeLabel !== eventTypeFilter
      ) {
        return false;
      }
      if (statusFilter !== "all" && deriveStatus(event) !== statusFilter) {
        return false;
      }
      if (fromUtcMilli !== null && event.startAtUtcMilli < fromUtcMilli) {
        return false;
      }
      if (toUtcMilli !== null && event.startAtUtcMilli > toUtcMilli) {
        return false;
      }
      return true;
    });
  }, [
    events,
    searchQuery,
    locationFilter,
    eventTypeFilter,
    statusFilter,
    dateFromFilter,
    dateToFilter,
  ]);

  const sortedEvents = useMemo(() => {
    if (!sort) {
      return filteredEvents;
    }
    const sortValue = (event: ManageEventRow): string | number => {
      switch (sort.column) {
        case "name":
          return event.title.toLowerCase();
        case "date":
          return event.startAtUtcMilli;
        case "ticketsSold":
          return event.attendeeCount;
        case "ticketRevenue":
          return event.ticketRevenueInCents;
        case "sponsorsSold":
          return event.sponsorsSold;
        case "sponsorshipRevenue":
          return event.sponsorshipRevenueInCents;
        case "totalRevenue":
          return event.ticketRevenueInCents + event.sponsorshipRevenueInCents;
        case "status":
        default:
          return statusMeta[deriveStatus(event)].label;
      }
    };
    const factor = sort.direction === "asc" ? 1 : -1;
    return [...filteredEvents].sort((a, b) => {
      const valueA = sortValue(a);
      const valueB = sortValue(b);
      if (valueA < valueB) {
        return -1 * factor;
      }
      if (valueA > valueB) {
        return 1 * factor;
      }
      return 0;
    });
  }, [filteredEvents, sort]);

  const pageCount = Math.max(1, Math.ceil(sortedEvents.length / pageSize));
  const currentPage = Math.min(page, pageCount);
  const pagedEvents = sortedEvents.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const locationFilterIsActive = locationFilter !== "all";
  const typeFilterIsActive = eventTypeFilter !== "all";
  const statusFilterIsActive = statusFilter !== "all";
  const dateFilterIsActive =
    dateFromFilter.length > 0 || dateToFilter.length > 0;

  const clearFilters = () => {
    setSearchQuery("");
    setLocationFilter("all");
    setEventTypeFilter("all");
    setStatusFilter("all");
    setDateFromFilter("");
    setDateToFilter("");
    setPage(1);
  };

  const toggleSort = (column: SortColumn) =>
    setSort((current) =>
      current?.column === column
        ? { column, direction: current.direction === "asc" ? "desc" : "asc" }
        : { column, direction: "asc" }
    );

  const refetchAll = async () => {
    await Promise.all([refetchEvents(), refetchAnalytics()]);
  };

  // ----- Exports -------------------------------------------------------------
  const handleExportAttendeesFor = async (eventId: string) => {
    const result = await exportAttendees({
      variables: { input: { chamberId, eventId } },
    });
    if (result.data?.exportCmpEventAttendeesCsv) {
      downloadCsv(result.data.exportCmpEventAttendeesCsv);
    }
  };
  const handleExportSponsorsFor = async (eventId: string) => {
    const result = await exportSponsors({
      variables: { input: { chamberId, eventId } },
    });
    if (result.data?.exportCmpEventSponsorsCsv) {
      downloadCsv(result.data.exportCmpEventSponsorsCsv);
    }
  };
  const handleExportAllEvents = async () => {
    const result = await exportAllEvents({ variables: { input: { chamberId } } });
    if (result.data?.exportCmpEventsCsv) {
      downloadCsv(result.data.exportCmpEventsCsv);
    }
  };
  const exportSelected = async (kind: "attendees" | "sponsors") => {
    if (selectedIds.length === 0) {
      setFeedback({
        severity: "error",
        message: "Select one or more events first using the checkboxes.",
      });
      return;
    }
    for (const eventId of selectedIds) {
      // eslint-disable-next-line no-await-in-loop
      await (kind === "attendees"
        ? handleExportAttendeesFor(eventId)
        : handleExportSponsorsFor(eventId));
    }
  };

  // ----- Status actions ------------------------------------------------------
  const runAction = async (event: ManageEventRow, action: ActionKey) => {
    const input = { chamberId, eventId: event.id };
    setActionLoading(true);
    try {
      switch (action) {
        case "publishPublic":
          await publishEvent({
            variables: {
              input: { ...input, visibility: CmpEventVisibilityEnum.Public },
            },
          });
          break;
        case "publishPrivate":
          await publishEvent({
            variables: {
              input: { ...input, visibility: CmpEventVisibilityEnum.Private },
            },
          });
          break;
        case "cancel":
          await cancelEvent({ variables: { input } });
          break;
        case "close":
          await completeEvent({ variables: { input } });
          break;
        case "setDraft":
          await unpublishEvent({ variables: { input } });
          break;
        case "delete":
          await deleteEvent({ variables: { input } });
          break;
        default:
          break;
      }
      await refetchAll();
      setFeedback({ severity: "success", message: "Event updated." });
    } catch (error) {
      setFeedback({
        severity: "error",
        message:
          error instanceof Error ? error.message : "Could not update the event.",
      });
    } finally {
      setActionLoading(false);
      setConfirmAction(null);
    }
  };

  const onActionSelected = (event: ManageEventRow, action: ActionKey) => {
    setActionMenu(null);
    if (action === "edit") {
      navigate(RoutePaths.chamberAdmin_events_update.replace(":eventId", event.id));
      return;
    }
    if (action === "exportAttendees") {
      handleExportAttendeesFor(event.id);
      return;
    }
    if (action === "exportSponsors") {
      handleExportSponsorsFor(event.id);
      return;
    }
    setConfirmAction({ event, action });
  };

  // Row selection is visual-only (no bulk actions in the manage screenshot
  // beyond the per-selection sponsor/attendee exports); log for QA tracing.
  const toggleSelected = (eventId: string) =>
    setSelectedIds((current) => {
      const next = current.includes(eventId)
        ? current.filter((id) => id !== eventId)
        : [...current, eventId];
      console.info("[ManageEvents] selected event rows:", next);
      return next;
    });

  const pagedIds = pagedEvents.map((event) => event.id);
  const allPagedSelected =
    pagedIds.length > 0 && pagedIds.every((id) => selectedIds.includes(id));
  const somePagedSelected = pagedIds.some((id) => selectedIds.includes(id));
  const toggleSelectAll = () =>
    setSelectedIds((current) => {
      const next = allPagedSelected
        ? current.filter((id) => !pagedIds.includes(id))
        : Array.from(new Set([...current, ...pagedIds]));
      console.info("[ManageEvents] selected event rows:", next);
      return next;
    });

  // DataTable columns (B7). "none" doubles as the initial no-sort sentinel
  // (it matches no sortable column, so every arrow stack renders grey exactly
  // as before) and the non-sortable Actions column key. Cell JSX is the old
  // TableCell content verbatim; the old cell classNames ride as cellSx
  // (text-gray-950 = #030712).
  const columns: ReadonlyArray<
    DataTableColumn<ManageEventRow, SortColumn | "none">
  > = [
    {
      key: "name",
      header: "Event Name",
      sortable: true,
      render: (event) => (
        <>
          <p className="font-bold text-gray-950">{event.title}</p>
          <p className="mt-1 text-xs text-gray-500">{event.eventTypeLabel}</p>
        </>
      ),
    },
    {
      key: "date",
      header: "Event Date",
      sortable: true,
      render: (event) => (
        <>
          {/* Date is bold in the manage screenshot, matching the
              bold event name weight. */}
          <p className="whitespace-nowrap font-bold text-gray-950">
            {formatDate(event.startAtUtcMilli)}
          </p>
          <p className="mt-1 whitespace-nowrap text-xs text-gray-500">
            {formatTimeRange(event.startAtUtcMilli, event.endAtUtcMilli)}
          </p>
        </>
      ),
    },
    {
      key: "ticketsSold",
      header: "Tickets Sold",
      sortable: true,
      cellSx: { minWidth: 150 },
      render: (event) => {
        const capacity = event.maxAttendeeCapacity || 0;
        const pct =
          capacity > 0
            ? Math.min(
                100,
                Math.round((event.attendeeCount / capacity) * 100)
              )
            : 0;
        const tierColor = ticketsSoldTierColor(pct);
        return (
          <>
            <p className="font-bold text-gray-950">
              {event.attendeeCount}/{capacity || "∞"}
            </p>
            <div className="mt-1.5 h-1.5 w-full max-w-[120px] rounded-full bg-gray-200">
              <div
                className="h-1.5 rounded-full"
                style={{ width: `${pct}%`, backgroundColor: tierColor }}
              />
            </div>
            <p
              className="mt-1.5 text-xs font-bold"
              style={{ color: tierColor }}
            >
              {pct}%
            </p>
          </>
        );
      },
    },
    {
      key: "ticketRevenue",
      header: "Ticket Revenue",
      sortable: true,
      cellSx: { whiteSpace: "nowrap", color: "#030712" },
      render: (event) => formatCurrencyFromCents(event.ticketRevenueInCents),
    },
    {
      key: "sponsorsSold",
      header: "Sponsors Sold",
      sortable: true,
      cellSx: { color: "#030712" },
      render: (event) => event.sponsorsSold,
    },
    {
      key: "sponsorshipRevenue",
      header: "Sponsorship Revenue",
      sortable: true,
      cellSx: { whiteSpace: "nowrap", color: "#030712" },
      render: (event) =>
        formatCurrencyFromCents(event.sponsorshipRevenueInCents),
    },
    {
      key: "totalRevenue",
      header: "Total Revenue",
      sortable: true,
      cellSx: { whiteSpace: "nowrap", color: "#030712" },
      render: (event) =>
        formatCurrencyFromCents(
          event.ticketRevenueInCents + event.sponsorshipRevenueInCents
        ),
    },
    {
      key: "status",
      header: "Status",
      sortable: true,
      render: (event) => {
        const status = deriveStatus(event);
        // StatusBadge text variant with no secondaryLabel renders the exact
        // former <span className style={{color}}> — byte-identical DOM.
        return (
          <StatusBadge
            variant="text"
            label={statusMeta[status].label}
            textClassName="text-sm font-bold"
            textColor={statusMeta[status].color}
          />
        );
      },
    },
    {
      key: "none",
      header: "Actions",
      width: "140px",
      headerCellSx: { fontWeight: 700, whiteSpace: "nowrap" },
      stickyRight: true,
      render: (event) => (
        <RowActionsTrigger
          onOpen={(el) => setActionMenu({ event, el })}
          sx={eventsActionButtonSx}
        />
      ),
    },
  ];

  return (
    <ManagePageShell
      className="mx-auto w-full max-w-[1600px] pb-10"
      headerOverride={headerOverride}
      statusMessage={feedback}
      onStatusMessageClose={() => setFeedback(null)}
    >
      {/* Total Event Analytics */}
      <div className="mb-4 mt-2">
        <h2 className="text-2xl font-bold text-gray-950">
          Total Event Analytics (Fiscal Year to Date)
        </h2>
        <p className="mt-1 text-base text-gray-700">
          See a quick overview of your chamber total year to date events
          analytics at a glance.
        </p>
      </div>
      <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        <AnalyticsCard
          label="Total Events YTD"
          value={String(analytics?.totalEvents ?? 0)}
          current={analytics?.totalEvents ?? 0}
          prior={analytics?.priorTotalEvents ?? 0}
        />
        <AnalyticsCard
          label="Total Event Registrations YTD"
          value={String(analytics?.totalRegistrations ?? 0)}
          current={analytics?.totalRegistrations ?? 0}
          prior={analytics?.priorTotalRegistrations ?? 0}
        />
        <AnalyticsCard
          label="Total Ticket Revenue YTD"
          value={formatCurrencyFromCents(analytics?.totalTicketRevenueInCents ?? 0)}
          current={analytics?.totalTicketRevenueInCents ?? 0}
          prior={analytics?.priorTotalTicketRevenueInCents ?? 0}
        />
        <AnalyticsCard
          label="Total Sponsors Sold YTD"
          value={String(analytics?.totalSponsorsSold ?? 0)}
          current={analytics?.totalSponsorsSold ?? 0}
          prior={analytics?.priorTotalSponsorsSold ?? 0}
        />
        <AnalyticsCard
          label="Total Sponsorship Revenue YTD"
          value={formatCurrencyFromCents(
            analytics?.totalSponsorshipRevenueInCents ?? 0
          )}
          current={analytics?.totalSponsorshipRevenueInCents ?? 0}
          prior={analytics?.priorTotalSponsorshipRevenueInCents ?? 0}
        />
        <AnalyticsCard
          label="Total Event Revenue YTD"
          value={formatCurrencyFromCents(analytics?.totalEventRevenueInCents ?? 0)}
          current={analytics?.totalEventRevenueInCents ?? 0}
          prior={analytics?.priorTotalEventRevenueInCents ?? 0}
        />
      </div>

      {/* Event Filters */}
      <div className="mb-3">
        <h2 className="text-lg font-bold text-gray-950">Event Filters</h2>
        <p className="text-sm text-gray-700">
          Use filters to easily sort information
        </p>
      </div>
      {/* FilterSearchField className is cleared to "" (its default adds an
          xl flex-grow track) and the page's minWidth-280 rides ahead of the
          shared filterFieldSx. Each FilterMenuButton sx undoes the shared
          width:100% and, when IDLE ONLY, restores this page's lighter
          #8F969E label without clobbering the shared active #111827. */}
      <FilterBar className="mb-8 flex flex-wrap items-center gap-3">
        <FilterSearchField
          value={searchQuery}
          onChange={(value) => {
            setSearchQuery(value);
            setPage(1);
          }}
          placeholder="Search by event name"
          className=""
          fullWidth={false}
          fieldSx={[{ minWidth: 280 }, filterFieldSx]}
        />
        <FilterMenuButton
          label="Filter by Location"
          onOpen={setLocationMenuAnchor}
          active={locationFilterIsActive}
          sx={
            locationFilterIsActive
              ? { width: "auto" }
              : { width: "auto", color: "#8F969E" }
          }
        />
        <FilterMenuButton
          label="Filter by Event Type"
          onOpen={setTypeMenuAnchor}
          active={typeFilterIsActive}
          sx={
            typeFilterIsActive
              ? { width: "auto" }
              : { width: "auto", color: "#8F969E" }
          }
        />
        <FilterMenuButton
          label="Filter by Status"
          onOpen={setStatusMenuAnchor}
          active={statusFilterIsActive}
          sx={
            statusFilterIsActive
              ? { width: "auto" }
              : { width: "auto", color: "#8F969E" }
          }
        />
        <FilterMenuButton
          label="Filter by Date"
          onOpen={setDateMenuAnchor}
          active={dateFilterIsActive}
          startIcon={<CalendarTodayOutlinedIcon />}
          sx={
            dateFilterIsActive
              ? { width: "auto" }
              : { width: "auto", color: "#8F969E" }
          }
        />
        {/* POL-013 (Q-026 answered: plural platform-wide) — the shared
            default label is already "Clear All Filters"; px/width restore
            the page's exact former geometry. Never disabled here. */}
        <ClearFiltersButton
          onClick={clearFilters}
          sx={[clearFiltersButtonSx, { px: 3, width: "auto" }]}
        />
      </FilterBar>

      {/* Filter dropdowns */}
      <Menu
        anchorEl={locationMenuAnchor}
        open={Boolean(locationMenuAnchor)}
        onClose={() => setLocationMenuAnchor(null)}
        PaperProps={{ sx: { borderRadius: "8px", minWidth: 240, py: 1 } }}
      >
        <MenuItem
          selected={locationFilter === "all"}
          onClick={() => {
            setLocationFilter("all");
            setLocationMenuAnchor(null);
            setPage(1);
          }}
        >
          All Locations
        </MenuItem>
        {locationOptions.map((location) => (
          <MenuItem
            key={location}
            selected={locationFilter === location}
            onClick={() => {
              setLocationFilter(location);
              setLocationMenuAnchor(null);
              setPage(1);
            }}
          >
            {location}
          </MenuItem>
        ))}
      </Menu>
      <Menu
        anchorEl={typeMenuAnchor}
        open={Boolean(typeMenuAnchor)}
        onClose={() => setTypeMenuAnchor(null)}
        PaperProps={{ sx: { borderRadius: "8px", minWidth: 240, py: 1 } }}
      >
        <MenuItem
          selected={eventTypeFilter === "all"}
          onClick={() => {
            setEventTypeFilter("all");
            setTypeMenuAnchor(null);
            setPage(1);
          }}
        >
          All Event Types
        </MenuItem>
        {eventTypeOptions.map((eventType) => (
          <MenuItem
            key={eventType.id}
            selected={eventTypeFilter === eventType.label}
            onClick={() => {
              setEventTypeFilter(eventType.label);
              setTypeMenuAnchor(null);
              setPage(1);
            }}
          >
            {eventType.label}
          </MenuItem>
        ))}
      </Menu>
      <Menu
        anchorEl={statusMenuAnchor}
        open={Boolean(statusMenuAnchor)}
        onClose={() => setStatusMenuAnchor(null)}
        PaperProps={{ sx: { borderRadius: "8px", minWidth: 240, py: 1 } }}
      >
        <MenuItem
          selected={statusFilter === "all"}
          onClick={() => {
            setStatusFilter("all");
            setStatusMenuAnchor(null);
            setPage(1);
          }}
        >
          All Statuses
        </MenuItem>
        {(Object.keys(statusMeta) as DerivedStatus[]).map((status) => (
          <MenuItem
            key={status}
            selected={statusFilter === status}
            onClick={() => {
              setStatusFilter(status);
              setStatusMenuAnchor(null);
              setPage(1);
            }}
          >
            {statusMeta[status].label}
          </MenuItem>
        ))}
      </Menu>
      <Menu
        anchorEl={dateMenuAnchor}
        open={Boolean(dateMenuAnchor)}
        onClose={() => setDateMenuAnchor(null)}
        PaperProps={{ sx: dateRangeMenuPaperSx }}
      >
        <div className="space-y-3">
          <div>
            <div className="text-sm font-extrabold leading-5 text-gray-900">
              Event date range
            </div>
            <div className="mt-0.5 text-xs font-medium leading-5 text-gray-500">
              Select one date or a from/to range.
            </div>
          </div>
          <div>
            <p className="mb-1 text-sm font-bold text-black">From</p>
            <CalendarDateInput
              ariaLabel="Event date from"
              mode="fullDate"
              placeholder="Select Date"
              iconPosition="right"
              value={dateFromFilter}
              onChange={(value) => {
                setDateFromFilter(value);
                setPage(1);
              }}
            />
          </div>
          <div>
            <p className="mb-1 text-sm font-bold text-black">To</p>
            <CalendarDateInput
              ariaLabel="Event date to"
              mode="fullDate"
              placeholder="Select Date"
              iconPosition="right"
              value={dateToFilter}
              onChange={(value) => {
                setDateToFilter(value);
                setPage(1);
              }}
            />
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button
              disabled={!dateFromFilter && !dateToFilter}
              onClick={() => {
                setDateFromFilter("");
                setDateToFilter("");
                setPage(1);
              }}
              size="small"
              sx={{
                color: "#4B5563",
                fontSize: 13,
                fontWeight: 800,
                textTransform: "none",
              }}
              type="button"
            >
              Clear dates
            </Button>
            <Button
              onClick={() => setDateMenuAnchor(null)}
              size="small"
              sx={{
                backgroundColor: cpaGold,
                color: "#fff",
                fontSize: 13,
                fontWeight: 800,
                px: 2,
                textTransform: "none",
                "&:hover": { backgroundColor: cpaGoldDark },
              }}
              type="button"
              variant="contained"
            >
              Done
            </Button>
          </div>
        </div>
      </Menu>

      {/* Manage Event List */}
      <div className="mb-4 flex flex-col justify-between gap-3 md:flex-row md:items-end">
        <div>
          <h2 className="text-lg font-bold text-gray-950">Manage Event List</h2>
          <p className="text-sm text-gray-700">
            Preview and edit event details and status
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <Button
            variant="contained"
            startIcon={<FileUploadOutlinedIcon />}
            disabled={exportingAll}
            onClick={handleExportAllEvents}
            sx={exportButtonSx}
          >
            Export All Event List
          </Button>
          <Button
            variant="contained"
            startIcon={<FileUploadOutlinedIcon />}
            onClick={() => exportSelected("sponsors")}
            sx={exportButtonSx}
          >
            Export Sponsors List
          </Button>
          <Button
            variant="contained"
            startIcon={<FileUploadOutlinedIcon />}
            onClick={() => exportSelected("attendees")}
            sx={exportButtonSx}
          >
            Export Attendee List
          </Button>
        </div>
      </div>

      {/* tableSx deliberately replaces (NOT extends) the shared dataTableSx —
          the Members default's fixed layout + tighter cell padding would
          visibly reflow this table. minWidth 1200 + MUI cell defaults are the
          page's exact former values. Pagination stays inside the table card
          per the manage screenshot; DataTable's pagination defaults
          (footer markup, "N / page" select, rounded gold pager, [10, 25, 50])
          are verbatim ManageEvents copies from Phase A. */}
      <DataTable<ManageEventRow, SortColumn | "none">
        ariaLabel="manage events table"
        columns={columns}
        rows={pagedEvents}
        getRowKey={(event) => event.id}
        sort={{
          column: (sort?.column ?? "none") as SortColumn | "none",
          direction: sort?.direction ?? "asc",
          // "none" is the non-sortable Actions column's key; renderSortLabel
          // never fires for it, so the guard is a type-narrowing no-op.
          onRequestSort: (column) => {
            if (column !== "none") {
              toggleSort(column);
            }
          },
        }}
        renderSortLabel={({ column, label, active, direction, onRequestSort }) => (
          <button
            type="button"
            onClick={() => onRequestSort(column)}
            className="inline-flex items-center gap-1 whitespace-nowrap font-bold text-gray-950"
            aria-label={`Sort by ${label}`}
          >
            {label}
            <span className="flex flex-col" aria-hidden>
              <ArrowDropUpIcon
                sx={{
                  color: active && direction === "asc" ? "#111827" : "#9CA3AF",
                  fontSize: 18,
                  mb: "-11px",
                }}
              />
              <ArrowDropDownIcon
                sx={{
                  color: active && direction === "desc" ? "#111827" : "#9CA3AF",
                  fontSize: 18,
                }}
              />
            </span>
          </button>
        )}
        showLoadingRow={eventsLoading && events.length === 0}
        loadingRowContent={
          // py-4 wrapper + DataTable's default 16px cell padding reproduces
          // the old centered !py-8 (32px) message-cell geometry.
          <div className="flex justify-center py-4">
            <CircularProgress size={22} sx={{ color: cpaGold }} />
          </div>
        }
        showEmptyRow={!eventsLoading && pagedEvents.length === 0}
        emptyRowContent={
          <div className="py-4 text-center text-gray-500">
            {sortedEvents.length !== events.length
              ? "No events match your filters."
              : "No events have been created yet."}
          </div>
        }
        containerClassName="w-full overflow-x-auto rounded-xl border border-gray-200 bg-white"
        tableSx={{ minWidth: 1200 }}
        rowClassName="align-top hover:bg-gray-50"
        // checkboxSx omitted → theme MuiCheckbox default (CMP gold per
        // Q-008), exactly the sx-less checkboxes this page rendered before.
        selection={{
          isRowSelected: (event) => selectedIds.includes(event.id),
          onToggleRow: (event) => toggleSelected(event.id),
          onToggleAll: toggleSelectAll,
          allSelected: allPagedSelected,
          someSelected: somePagedSelected,
          headerAriaLabel: "select all events",
          getRowAriaLabel: (event) => `select ${event.title}`,
        }}
        pagination={{
          page: currentPage,
          pageCount,
          pageSize,
          totalCount: sortedEvents.length,
          itemNoun: "events",
          onPageChange: setPage,
          onPageSizeChange: (size) => {
            setPageSize(size);
            setPage(1);
          },
        }}
      />

      {/* Single page-level Actions menu (RowActionMenu usage contract) —
          paper + destructive-red defaults are documented verbatim-ManageEvents
          copies; itemSx carries the page's former per-item sx. RowActionMenu
          closes first, then acts, matching onActionSelected's leading
          setActionMenu(null). */}
      <RowActionMenu
        actions={actionMenu ? getRowActions(actionMenu.event) : []}
        anchorEl={actionMenu?.el ?? null}
        open={Boolean(actionMenu)}
        onClose={() => setActionMenu(null)}
        onAction={(key) => {
          if (actionMenu) {
            onActionSelected(actionMenu.event, key);
          }
        }}
        itemSx={{ fontSize: 15, py: 1.25 }}
      />

      {/* Update Status / Delete confirmation */}
      <UpdateStatusModal
        open={Boolean(confirmAction)}
        variant={confirmAction?.action === "delete" ? "delete" : "status"}
        title={
          confirmAction?.action === "delete"
            ? "Are you sure you want to delete this event?"
            : "Are you sure you want to update the status of this event?"
        }
        loading={actionLoading}
        onConfirm={() =>
          confirmAction && runAction(confirmAction.event, confirmAction.action)
        }
        onCancel={() => setConfirmAction(null)}
      />
    </ManagePageShell>
  );
};

const ManageEvents = (): React.ReactElement => {
  const location = useLocation();
  const pixelQaState = getDashboardPixelQaState(
    location.search,
    "manage-events"
  );

  if (pixelQaState) {
    return <EventDashboardPixelQa state={pixelQaState} />;
  }

  return <ManageEventsContent />;
};

export default ManageEvents;
